import { useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  featureFlagsApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';

// Aligned with orca-backend's poll cycle (every 60 min by default).
// 15 min gives fresh data without hammering the backend with per-entity fan-out on every poll.
export const POLL_INTERVAL_MS = 900_000;

const MAX_ALERTS = 5;

// The orca feature flag gates the Orca tab in EntityPage.
// The banner must respect the same flag so clicking through always lands on a visible tab.
const ORCA_FEATURE_FLAG = 'orca';

export interface OrcaAlert {
  id: string;
  entityRef: string;
  entityName: string;
  orcaPath: string;
  critical: number;
  high: number;
  totalHighSeverity: number;
}

export interface UseOrcaAlertsResult {
  alerts: OrcaAlert[];
  overflowCount: number;
}

interface OrcaAlertCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  informational: number;
}

interface OrcaSummaryResponse {
  kubernetesId: string;
  fetchedAt: string;
  alertCounts: OrcaAlertCounts;
}

// Mirrors the same annotation-extraction logic in OrcaAlertsCard.tsx.
// kubernetes-id takes precedence over the github.repository label from the selector.
function deriveKubernetesId(entity: Entity): string | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const k8sId = annotations['backstage.io/kubernetes-id'];
  if (k8sId) return k8sId.toLowerCase();

  const selectorMatch = annotations['backstage.io/kubernetes-label-selector']?.match(
    /github\.repository=([a-zA-Z0-9._/-]+)/,
  );
  return selectorMatch?.[1]?.toLowerCase();
}

export function useOrcaAlerts(): UseOrcaAlertsResult {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const featureFlagsApi = useApi(featureFlagsApiRef);

  const [state, setState] = useState<UseOrcaAlertsResult>({
    alerts: [],
    overflowCount: 0,
  });
  const mountedRef = useRef(true);
  const failedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    failedRef.current = false;

    const poll = async () => {
      try {
        // Respect the same feature flag that gates the Orca tab in EntityPage.
        // Without this, clicking the banner can land on a page where the tab doesn't exist.
        if (!featureFlagsApi.isActive(ORCA_FEATURE_FLAG)) {
          if (mountedRef.current) setState({ alerts: [], overflowCount: 0 });
          return;
        }

        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;

        const groupsResponse = await catalogApi.getEntities({
          filter: { kind: 'Group', 'relations.hasMember': userEntityRef },
          fields: ['kind', 'metadata.namespace', 'metadata.name'],
        });

        if (groupsResponse.items.length === 0) {
          failedRef.current = false;
          if (mountedRef.current) setState({ alerts: [], overflowCount: 0 });
          return;
        }

        const groupRefs = groupsResponse.items.map(g => stringifyEntityRef(g));

        const componentsResponse = await catalogApi.getEntities({
          filter: { kind: 'Component', 'relations.ownedBy': groupRefs },
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'metadata.annotations',
          ],
        });

        const k8sEntities = componentsResponse.items.filter(
          e => deriveKubernetesId(e) !== undefined,
        );

        if (k8sEntities.length === 0) {
          failedRef.current = false;
          if (mountedRef.current) setState({ alerts: [], overflowCount: 0 });
          return;
        }

        // Resolve base URL once per cycle, not once per entity.
        const baseUrl = await discoveryApi.getBaseUrl('orca');

        // Fetch all entities in parallel — auth is per-request via fetchApi, so this is safe.
        // Promise.allSettled lets a single failing entity not block the rest.
        const entityResponses = await Promise.allSettled(
          k8sEntities.map(async entity => {
            const kubernetesId = deriveKubernetesId(entity)!;
            const url = `${baseUrl}/security-summary?kubernetesId=${encodeURIComponent(kubernetesId)}`;
            const res = await fetchApi.fetch(url);
            if (res.status === 404) return null;
            if (!res.ok) return null;
            const summary: OrcaSummaryResponse = await res.json();
            return { entity, summary };
          }),
        );

        // If every fetch failed (network outage), preserve previous state.
        if (entityResponses.every(r => r.status === 'rejected')) return;

        const rawAlerts: OrcaAlert[] = [];

        for (const result of entityResponses) {
          if (result.status === 'rejected') continue;
          if (!result.value) continue;

          const { entity, summary } = result.value;
          const { critical, high } = summary.alertCounts;
          if (critical + high === 0) continue;

          const entityRef = stringifyEntityRef(entity);
          const namespace = entity.metadata.namespace ?? 'default';
          const kind = entity.kind.toLowerCase();
          const name = entity.metadata.name;

          rawAlerts.push({
            id: entityRef,
            entityRef,
            entityName: name,
            orcaPath: `/catalog/${namespace}/${kind}/${name}/orca`,
            critical,
            high,
            totalHighSeverity: critical + high,
          });
        }

        rawAlerts.sort((a, b) => {
          if (b.critical !== a.critical) return b.critical - a.critical;
          if (b.high !== a.high) return b.high - a.high;
          return a.entityName.localeCompare(b.entityName);
        });

        const alerts = rawAlerts.slice(0, MAX_ALERTS);
        const overflowCount = Math.max(0, rawAlerts.length - MAX_ALERTS);

        failedRef.current = false;
        if (mountedRef.current) setState({ alerts, overflowCount });
      } catch (err) {
        if (!failedRef.current) {
          // eslint-disable-next-line no-console
          console.warn('useOrcaAlerts: poll failed, will retry silently', err);
          failedRef.current = true;
        }
      }
    };

    void poll();
    const interval = setInterval(() => void poll(), POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [identityApi, catalogApi, discoveryApi, fetchApi, featureFlagsApi]);

  return state;
}
